/**
 * Keeping a reader's other tabs on the language they just picked.
 *
 * The language cookie is shared between tabs, but noticing a change is not:
 * `useCookies` reads its snapshot with `doNotUpdate`, so it only refreshes when
 * something calls `update()`. In practice that happened either through Chromium's
 * `cookieStore` change events — which Firefox and Safari do not implement — or
 * incidentally, because unrelated code did a live cookie read and woke every
 * subscriber. A quiet tab in a browser without `cookieStore` kept the old
 * language until it was reloaded.
 *
 * So the change is announced explicitly. The message is only a notification: the
 * cookie stays the single source of truth and each tab re-reads it, rather than
 * trusting a language carried in the payload. A cookie write that silently failed
 * therefore does not get applied anywhere, which is the behaviour we want.
 *
 * Scope is the origin, so this covers a reader's Studio tabs. Tabs on a sister
 * app are a different origin and still depend on the shared cookie.
 */
import * as React from 'react'
import { useRefreshLangCookie } from 'cf-i18n/cookies'

const CHANNEL_NAME = 'cf-studio-language'
const MESSAGE = 'language-changed'

function openChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') {
    return null
  }

  return new BroadcastChannel(CHANNEL_NAME)
}

/**
 * Opened once and kept for the lifetime of the document: closing a channel in the
 * same task as the post drops the message.
 */
let publishChannel: BroadcastChannel | null | undefined

/** Tells this reader's other tabs that the language cookie changed. */
export function publishStudioLanguageChange(): void {
  publishChannel ??= openChannel()

  publishChannel?.postMessage(MESSAGE)
}

/** Applies a language the reader picked in one of their other tabs. */
export function useStudioLanguageBroadcast(): void {
  const refreshLangCookie = useRefreshLangCookie()

  React.useEffect(() => {
    const channel = openChannel()

    if (!channel) {
      return
    }

    // Re-reading is enough: it notifies every cookie reader in this tab, so the
    // language resolver recomputes and the i18n and URL syncs follow from there.
    channel.onmessage = () => refreshLangCookie()

    return () => channel.close()
  }, [refreshLangCookie])
}
